import { InfoOutlined } from "@mui/icons-material"
import {
  Box,
  Stack,
  TableRow,
  Typography,
  useMediaQuery,
  Theme,
  Paper,
  Tooltip,
} from "@mui/material"
import { TableCell } from "@mui/material"
import { memo, useEffect, useMemo, useState } from "react"

import { PortfolioAgentInfo } from "@/api/portfolio-agents-api"
import { RichTokenInfo, usePortfolio } from "@/api/portfolio-api"
import { IdBlock } from "@/components/IdBlock"
import { HeaderCell, InMemoryTable } from "@/components/InMemoryTable"
import { LoadingSkeletons } from "@/components/LoadingSkeletons"
import { PriceDisplay } from "@/components/PriceDisplay"
import { NumberFont } from "@/components/RootLayout/fonts"
import { TokenAmountBlock } from "@/components/TokenAmountBlock"
import { TokenAvatar } from "@/components/TokenAvatar"
import { UsdAmountBlock } from "@/components/UsdAmountBlock"
import { REFRESH_INTERVAL } from "@/settings"
import { truncateId } from "@/utils/data-utils"
import { parseBigIntAsNumber } from "@/utils/format"

type EntityPortfolioProps = {
  entityId: string
  onPortfolioValue?: (value: number | null) => void
  isPortfolioAgent?: boolean
  agentInfo?: PortfolioAgentInfo
}

function EntityPortfolioBase(props: EntityPortfolioProps) {
  const { entityId, onPortfolioValue, isPortfolioAgent = false, agentInfo } = props

  const [, setRefresh] = useState(0)
  const [tokens] = usePortfolio(entityId)
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"))

  const data = useMemo(() => tokens?.filter((token) => token.balance > 0) || [], [tokens])

  const totalValue = useMemo(() => {
    if (tokens === null) return null
    const val = data.reduce((acc, token) => acc + (token.value ?? 0), 0)
    onPortfolioValue?.(val)
    return val
  }, [tokens, data, onPortfolioValue])

  useEffect(() => {
    const interval = setInterval(() => {
      setRefresh(Math.random())
    }, REFRESH_INTERVAL)
    return () => {
      clearInterval(interval)
    }
  }, [])

  if (tokens === null) {
    return <LoadingSkeletons />
  }

  return (
    <Paper sx={{ border: 0, paddingX: 2, paddingY: 2 }}>
      <Box>
        <Typography
          sx={{
            marginTop: 2,
            marginBottom: 1,
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          }}
          variant="h4"
        >
          Tokens{" "}
          {!!totalValue && (
            <>
              •{" "}
              <Typography fontFamily={NumberFont} variant="inherit" component="span">
                <UsdAmountBlock amount={totalValue} />
              </Typography>
            </>
          )}
        </Typography>

        {isMobile ? (
          <Box sx={{ background: "var(--mui-palette-paperAlt)" }}>
            {data.map((row: RichTokenInfo) => (
              <TableRow
                key={row.id}
                sx={{
                  background: "var(--mui-palette-paperAlt)",
                }}
              >
                <TableCell colSpan={4}>
                  <Stack spacing={1}>
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent={"space-between"}
                    >
                      <Stack direction="row" alignItems="center" gap={1}>
                        <TokenAvatar tokenId={row.id} />
                        <IdBlock
                          label={row.ticker || truncateId(row.id)}
                          value={row.id}
                          href={`/token/${row.id}`}
                        />
                      </Stack>

                      {row.price !== undefined && (
                        <Typography variant="body2">
                          Value: <UsdAmountBlock amount={row.value} />
                        </Typography>
                      )}
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent={"space-between"}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Balance:{" "}
                        <TokenAmountBlock
                          amount={row.balance}
                          tokenId={row.id}
                          hideCopyToClipboard
                          needsParsing
                        />
                      </Typography>
                      {row.price !== undefined && (
                        <Typography variant="body2" color="text.secondary">
                          Price: <UsdAmountBlock amount={row.price} />
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </Box>
        ) : (
          <Box>
            <InMemoryTable
              initialSortField="value"
              initialSortDir="desc"
              data={data}
              headerCells={[
                { field: "tokenId", label: "Token name" },
                { label: "Amount", align: "right" },
                { label: "Price", align: "right" },

                ...((isPortfolioAgent
                  ? [
                      {
                        label: (
                          <Stack
                            direction={"row"}
                            alignItems={"center"}
                            gap={0.5}
                            justifyContent={"flex-end"}
                          >
                            <span>Paper Value</span>
                            <Tooltip title="Your holdings multiplied by the current token price, reflecting their ideal worth under perfect conditions. It’s your theoretical maximum return.">
                              <InfoOutlined fontSize="small" />
                            </Tooltip>
                          </Stack>
                        ),
                        align: "right",
                        field: "value",
                      },
                      {
                        label: (
                          <Stack
                            direction={"row"}
                            alignItems={"center"}
                            gap={0.5}
                            justifyContent={"flex-end"}
                          >
                            <span>Realizable Value</span>
                            <Tooltip title="Estimated USD if selling your entire token balance in one transaction. This value fluctuates based on real-time liquidity in pools and market volatility, and is not guaranteed.">
                              <InfoOutlined fontSize="small" />
                            </Tooltip>
                          </Stack>
                        ),
                        align: "right",
                      },
                    ]
                  : [{ label: "Total Value", align: "right", field: "value" }]) as HeaderCell[]),
              ]}
              renderRow={(row: RichTokenInfo) => (
                <TableRow
                  key={row.id}
                  sx={{
                    background: "var(--mui-palette-paperAlt)",
                  }}
                >
                  <TableCell>
                    <Stack
                      direction="row"
                      alignItems="center"
                      gap={1}
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 700,
                      }}
                    >
                      <TokenAvatar tokenId={row.id} />
                      <IdBlock
                        label={row.ticker || truncateId(row.id)}
                        value={row.id}
                        href={`/token/${row.id}`}
                      />
                    </Stack>
                  </TableCell>
                  <TableCell align="right" variant="number">
                    <TokenAmountBlock
                      amount={row.balance}
                      tokenId={row.id}
                      hideCopyToClipboard
                      needsParsing
                    />
                  </TableCell>
                  <TableCell align="right" variant="number">
                    {row.price !== undefined && <UsdAmountBlock amount={row.price} />}
                  </TableCell>

                  {!isPortfolioAgent && (
                    <TableCell align="right" variant="number">
                      {row.price !== undefined && <UsdAmountBlock amount={row.value} />}
                    </TableCell>
                  )}

                  {isPortfolioAgent && (
                    <>
                      <TableCell align="right" variant="number">
                        {agentInfo !== undefined && (
                          <PriceDisplay
                            token={agentInfo["Quote-Token"]}
                            amount={Number(
                              parseBigIntAsNumber(
                                agentInfo.Holdings.entries.find((entry) => entry.token === row.id)
                                  ?.netValue || "0",
                                Number(agentInfo["Quote-Token-Denomination"]),
                              ),
                            )}
                            showOnlyUsd
                          />
                        )}
                      </TableCell>
                      <TableCell align="right" variant="number">
                        {agentInfo !== undefined && (
                          <PriceDisplay
                            token={agentInfo["Quote-Token"]}
                            amount={Number(
                              parseBigIntAsNumber(
                                agentInfo.Holdings.entries.find((entry) => entry.token === row.id)
                                  ?.realNetValue || "0",
                                Number(agentInfo["Quote-Token-Denomination"]),
                              ),
                            )}
                            showOnlyUsd
                          />
                        )}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              )}
            />
          </Box>
        )}
      </Box>
    </Paper>
  )
}

export const EntityPortfolio = memo(EntityPortfolioBase)
